// eslint-disable-next-line no-restricted-imports
import {
	Context,
	AppIntent,
	OpenError,
	ResolveError,
	ContextType,
	AppMetadata,
	ResultError,
	AppIdentifier,
} from "@finos/fdc3";

import { AppDefinition, ResolverChannelMeta } from "../../platform/services/Interop/types";
import { WindowDescriptor } from "../../platform/services/window/types";

// Returns a new type that equals the member of an original type
export type Member<T, K extends keyof T> = T[K];

// Multiple entries can be associated with a single key. Use for add/remove eventListeners.
export type MultiMap<T> = Record<string, Array<T>>;

/**
 * MultiMap add
 * @param records
 * @param index
 * @param handler
 */
export const mmAdd = (records: MultiMap<any>, index: string, handler: any): any[] => {
	const handlers = records[index];
	const functionArray = handlers ? handlers : [];
	return functionArray.concat([handler]);
};

/**
 * Concats an item to an array, eliminating any dups, and creating the array if it is null or undefined
 * @param arr
 * @param item
 */
export const concatNoDups = (arr: any[] | null | undefined, item: any | any[]): any[] => {
	const itemArray = Array.isArray(item) ? item : [item];
	const newArray = arr ? itemArray.concat(arr) : itemArray;
	return Array.from(new Set(newArray));
};

/**
 * MultiMap remove
 * @param records
 * @param index
 * @param handler
 */
export const mmRemove = (records: MultiMap<any>, index: string, handler: any): any[] => {
	const handlers = records[index];
	const functionArray = handlers ? handlers : [];
	return functionArray.filter((func) => func !== handler);
};

export const errorAsString = (err: any): string => err?.message ?? err ?? null;

export const GLOBAL_CHANNEL = "global";

export const SERVER_CHANNEL = "FSBL.FDC3.Interop.Server";

export const INTENT_RESOLVER_CHANNEL = "FSBL.FDC3.intent.resolver";

export const EMBEDDED_TITLEBAR_INDICATOR_STRING = "(TitleBar)";

export type ResolutionCandidate = {
	meta: AppMetadata;
	appId: string;
	uuid?: string;
	windowName?: string;
	desktopAgent?: string;
	pending?: boolean;
};

export type InteropConfigFlags = {
	joinMultipleChannels: boolean;
	fdc3_1_2_compatibility: boolean;
};

/**
 * A list of intent resolutions that are possible for a given intent and optional context.
 *
 * Arrays of Resolutionlist's are used in raiseIntent to deal with the raiseIntentByContext, which might result
 * in several resolution lists (one list for each possible intent that is associated with the requested context).
 *
 * For instance, raiseIntentByContext({"symbol" : "IBM"}) could result in:
 * contextType("symbol") => intent("Quote") => QuotesApp, RFQApp, ...
 * contextType("symbol") => intent("News") => NewsApp, TwitterApp, ...
 * contextType("symbol") => intent("Chart") => ChartApp, SnapshotChartApp, ...
 */
export type ResolutionList = {
	intent: {
		name: string;
		displayName: string;
	};
	// Apps that may be launched to handle the intent
	launchableApps: ResolutionCandidate[];
	// Apps that are open and ready to handle the intent
	openApps: ResolutionCandidate[];
	// Apps that are open and declare their ability to handle the intent, but which haven't yet registered a handler
	pendingApps: ResolutionCandidate[];
	context?: ContextAny;
};

export type IntentTargetList = {
	resolutionList: ResolutionList[];
	// the originatingAppName field is displayed by the UI Resolver to indicate the source of the request. Also a pass-though value used for auto-associate.
	originatingAppName?: string;
	// the originatingAppId field is a pass-though value used for auto-associate.
	originatingAppId?: string;
	// the originatingAppWindow field is a pass-though value used for auto-associate.
	originatingAppWindow?: string;
};

export type ResolverResolution = {
	// the originatingAppName field is displayed by the UI Resolver to indicate the source of the request. Also a pass-though value used for auto-associate.
	originatingAppName?: string;
	// the originatingAppId field is a pass-though value used for auto-associate.
	originatingAppId?: string;
	// the originatingAppWindow field is a pass-though value used for auto-associate.
	originatingAppWindow?: string;
	intent?: string;
	selectedApp?: AppMetadata;
	windowName?: string; // windowName must be set if and only if the selected app is already open; otherwise should be undefined
	rememberChoice?: boolean; // if true then remember the resolver choice
	error?: ResolveError; // if error set then the other properties will be ignored (i.e. the intent resolution failed)
};

export type AuthenticationToken = {
	// Signature used for the token; used by the InteropService to authenticate the app.
	signatureString: string;

	// "dynamic" indicates the signatureString was allocated by the window service -- it must match the value maintained within the InteropService (see setInteropRegistrationData);
	// "static" indicates the signatureString was preallocated externally -- it must conform to the rules defined for static authentication.
	// "static-jwt" indicates the signatureString is a JSON Web Token -- it must  conform to the rules defined for static JWT authentication.
	// "unauthenticated" indicates that the app cannot provide any authentication information. The name of the app may still be available in the meta.
	signatureStringType: "dynamic" | "static" | "static-jwt" | "unauthenticated";

	// The meta.name field must be assigned a valid value, matching the appID within Finsemble's AppD configuration; otherwise Finsemble won't fully recognize the app
	meta: AppMetadata;

	// If timestamp is undefined or 0 it will be ignored; otherwise it should be set to equivalent of JavaScript's Date.now() to use in InteropService's time-to-live (TTL) calculation.
	// When "static-jwt" is used, the timestamp will be set within Finsemble to the JWT's iot field (which is set when the JWT is created).
	timestamp?: number;
};

export const AnonymousAuthenticationToken: AuthenticationToken = {
	signatureString: "",
	signatureStringType: "unauthenticated",
	meta: {
		name: "Anonymous app",
		appId: "Anonymous appId",
	},
};

export type IntentResolverHandler = (choices: IntentTargetList) => Promise<ResolverResolution>;

// Context is just an interface for the meta information. We need to extend it in order to add data.
export type ContextAny = Context & { [data: string]: any };

export type ContextMap = { [contextType: string]: { context: ContextAny; senderUuid: string } };

/**
 * The following are the Flux Standard Actions (FSA) that are exchanged between the Desktop Agent
 * and the Interop Server.
 */

export type InteropMeta = {
	uuid: string;
	appId?: string;
	originatorUuid?: string;
	originatorAppId?: string;
	desktopAgent?: string;
	// For web apps, the document.location.href of the app
	href?: string;
	// For native apps, the full path of the executable
	path?: string;
	// the underlying transport the message originated from; only set in InteropService for register message
	authenticatedTransport?: object;
	// the routerClient the message originated from; only set in InteropService for register message
	routerOrigin?: string;
	// optional remote destination for some cases
	remoteDestination?: string;
	desktopAgentSource?: string;
};

export type InteropRegister = {
	type: "register";
	payload: {
		authenticationToken: AuthenticationToken;
		clientChannel: string;
		// Typically set to the window name value, but might be an alternate unique identifier to uniquely represent a non-chromium-window UI or a client without a window.
		// Window name is bound to the underlying client UUID.  When the Resolver UI identifies an existing app for routing a raiseIntent, it does so using  windowName.
		windowName: string;
	};
	meta: InteropMeta;
};

export type InteropHeartbeat = {
	type: "heartbeat";
	meta: InteropMeta;
};

export type InteropUnregister = {
	type: "unregister";
	meta: InteropMeta;
};

export type InteropBroadcast = {
	type: "broadcast";
	payload: {
		context: ContextAny;
		channelType?: InteropChannelType;
	};
	meta: InteropMeta & {
		channel?: string;
		// if set, contains either "*" or context type; when set, then the FDC3 client should deliver the message only to the specified subscriber.
		targetSubscriberId?: string;
	};
};

export type InteropSystemChannels = {
	type: "systemChannels";
	payload: {
		channels: string[];
		systemChannelMeta?: ResolverChannelMeta;
	};
	meta: InteropMeta;
};

export type InteropSystemChannelRequest = {
	type: "systemChannelRequest";
	meta: InteropMeta;
};

export type InteropCurrentContextRequest = {
	type: "currentContextRequest";
	payload: {
		contextType?: string;
	};
	meta: InteropMeta & {
		channel?: string;
	};
};

export type InteropCurrentContext = {
	type: "currentContext";
	payload: {
		context: Context | null;
	};
	meta: InteropMeta;
};

export type InteropOpenRequest = {
	type: "open";
	payload: {
		requestingAppId: string;
		app: AppIdentifier | string;
		context?: Context;
	};
	meta: InteropMeta;
};

export type InteropGetSystemChannels = {
	type: "open";
	payload: {
		target: AppMetadata;
		context?: Context;
	};
	meta: InteropMeta;
};

export type InteropOpenResponse = {
	type: "openResponse";
	payload: {
		appIdentifier?: AppIdentifier;
		error?: OpenError;
	};
	meta?: InteropMeta;
};

export type InteropFindIntentRequest = {
	type: "findIntent";
	payload: {
		intent: string;
		context?: Context;
		resultType?: string;
	};
	meta: InteropMeta;
};

export type InteropFindIntentResponse = {
	type: "findIntentResponse";
	payload: {
		appIntent?: AppIntent;
		error?: ResolveError | null;
	};
	meta: InteropMeta;
};

export type InteropFindIntentsByContext = {
	type: "findIntentsByContext";
	payload: {
		context?: Context;
		resultType?: string;
	};
	meta: InteropMeta;
};

export type InteropIntentsList = {
	type: "intentsList";
	payload: {
		error?: ResolveError | null;
		intents?: AppIntent[];
	};
	meta: InteropMeta;
};

export type InteropIntent = {
	type: "intent";
	payload: {
		intent: string;
		context: Context;
		requestId: string;
	};
	meta: InteropMeta;
};

export type InteropRaiseIntent = {
	type: "raiseIntent";
	payload: {
		intent: string | null;
		target?: AppIdentifier | String;
		context?: Context;
		requestId: string;
		forContext: boolean;
		bridgeIntent?: AppIntent | undefined;
	};
	meta: InteropMeta;
};

export type InteropRaiseIntentResponse = {
	type: "intentResolution";
	payload: {
		error?: ResolveError | null;
		data?: {
			source: AppMetadata;
			intent: string;
			version?: string;
		};
	};

	meta?: InteropMeta;
};

export type InteropChannelType = "user" | "app" | "private";
export type InteropChannel = { channelId: string; type: InteropChannelType };
export type InteropIntentResult = Context | InteropChannel;

export type InteropRaiseIntentResult = {
	type: "intentResult";
	payload: {
		intentResultError?: ResultError;
		intentResult?: InteropIntentResult;
		intent: string;
		requestId: string;
	};

	meta?: InteropMeta;
};

export type PrivateChannelEventSubscriptionType = "onAddContextListener" | "onUnsubscribe" | "onDisconnect";

export type InteropPrivateChannelEvent = {
	type: "privateChannelEvent";
	payload: {
		subscribeType: PrivateChannelEventSubscriptionType;
		channel: string;
		context?: string;
		targetSubscriberId: string;
	};
	meta: InteropMeta;
};

export type InteropSubscribePrivateChannelEvent = {
	type: "subscribePrivateChannelEvent";
	payload: {
		subscribeType: PrivateChannelEventSubscriptionType;
		channel?: string;
		targetSubscriberId?: string;
	};
	meta: InteropMeta;
};

export type InteropUnsubscribePrivateChannelEvent = {
	type: "unsubscribePrivateChannelEvent";
	payload: {
		subscribeType: "onAddContextListener" | "onUnsubscribe" | "onDisconnect";
		channel?: string;
		targetSubscriberId?: string;
	};
	meta: InteropMeta;
};

export type InteropChannelDisconnect = {
	type: "channelDisconnect";
	meta: InteropMeta & {
		channel: string;
		targetSubscriberId?: string;
	};
};

export type InteropSubscribeContext = {
	type: "subscribeContext";
	payload: {
		contextType: ContextType;
	};
	meta: InteropMeta & {
		channel?: string;
		targetSubscriberId?: string;
	};
};

export type InteropUnsubscribeContext = {
	type: "unsubscribeContext";
	payload: {
		contextType?: ContextType;
	};
	meta: InteropMeta & {
		channel?: string;
	};
};

export type InteropSubscribeIntent = {
	type: "subscribeIntent";
	payload: {
		intent: string;
	};
	meta: InteropMeta;
};

export type InteropUnsubscribeIntent = {
	type: "unsubscribeIntent";
	payload: {
		intent: string;
	};
	meta: InteropMeta;
};

export type InteropRemoveUIResolverChoice = {
	type: "removeUIResolverChoice";
	payload: {
		intent: string;
		contextType: string;
		appWindow: string;
	};
};

export type InteropJoinChannels = {
	type: "joinChannels";
	payload: {
		channels: string[];
	};
	meta: InteropMeta;
};

export type InteropJoinChannelsResponse = {
	type: "joinChannelsResponse";
	payload: {
		error?: string;
	};
	meta: InteropMeta;
};

/**
 * If payload.channnels is omitted then *all* joined channels are removed.
 */
export type InteropLeaveChannels = {
	type: "leaveChannels";
	payload: {
		channels?: string[];
	};
	meta: InteropMeta;
};

export type InteropAddAppDefinitions = {
	type: "addAppDefinitions";
	payload: {
		apps: AppDefinition[];
	};
	meta: InteropMeta;
};

export type InteropRemoveAppDefinitions = {
	type: "removeAppDefinitions";
	payload: {
		apps: string[];
	};
	meta: InteropMeta;
};

export type InteropRegisterUIResolver = {
	type: "registerUIResolver";
	meta: InteropMeta;
};

export type InteropGetCurrentChannels = {
	type: "getCurrentChannels";
	meta: InteropMeta;
};

export type InteropGetCurrentChannelsResponse = {
	type: "interopGetCurrentChannelsResponse";
	payload: {
		error?: string;
		data?: {
			channel: string;
		}[];
		systemChannelMeta?: ResolverChannelMeta;
	};
	meta?: InteropMeta;
};

export type InteropGetAppMetadata = {
	type: "getAppMetadata";
	app: AppIdentifier;
	meta: InteropMeta;
};

export type InteropGetAppMetadataResponse = {
	type: "interopGetAppMetadataResponse";
	payload: {
		error?: string;
		appMetadata?: AppMetadata;
	};
	meta?: InteropMeta;
};

export type InteropFindInstances = {
	type: "findInstances";
	app: AppIdentifier;
	meta: InteropMeta;
};

export type InteropFindInstancesResponse = {
	type: "interopFindInstancesResponse";
	payload: {
		instances: Array<AppIdentifier>;
		error?: string;
	};
	meta: InteropMeta;
};

export type InteropSetRegistrationData = {
	type: "setInteropRegistrationData";
	payload: {
		windowDescriptor: WindowDescriptor;
	};
	meta: InteropMeta;
};

export type InteropMessage =
	| InteropRegister
	| InteropUnregister
	| InteropBroadcast
	| InteropSystemChannels
	| InteropSystemChannelRequest
	| InteropCurrentContextRequest
	| InteropCurrentContext
	| InteropOpenRequest
	| InteropOpenResponse
	| InteropFindIntentRequest
	| InteropFindIntentResponse
	| InteropFindIntentsByContext
	| InteropHeartbeat
	| InteropIntent
	| InteropIntentsList
	| InteropJoinChannels
	| InteropJoinChannelsResponse
	| InteropLeaveChannels
	| InteropRaiseIntent
	| InteropRaiseIntentResponse
	| InteropRaiseIntentResult
	| InteropSubscribeContext
	| InteropUnsubscribeContext
	| InteropSubscribeIntent
	| InteropUnsubscribeIntent
	| InteropAddAppDefinitions
	| InteropRemoveAppDefinitions
	| InteropGetCurrentChannels
	| InteropGetCurrentChannelsResponse
	| InteropGetAppMetadata
	| InteropGetAppMetadataResponse
	| InteropFindInstances
	| InteropFindInstancesResponse
	| InteropRegisterUIResolver
	| InteropSetRegistrationData
	| InteropPrivateChannelEvent
	| InteropSubscribePrivateChannelEvent
	| InteropUnsubscribePrivateChannelEvent
	| InteropChannelDisconnect;

export type InteropType = Member<InteropMessage, "type">;
